import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { UserMission, MissionActionRequirement, Mission } from '../lib/gamification/types';
import { getUserActiveMissions, getUserCompletedMissions, getAllAvailableMissions, assignMissionToUser } from '../lib/gamification/missionService';
import { redeemMissionReward } from '../lib/gamification/gamificationService';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, CheckCircle2, Target, Gift, ChevronDown, ChevronUp, Award, Coins, History, Search, Star, PlayCircle } from 'lucide-react';
import { formatDistanceToNow, endOfDay, endOfWeek } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'react-hot-toast';
import { Progress } from '@/components/ui/progress';
import { Badge as UiBadge } from '@/components/ui/badge';

const UserMissionsPage: React.FC = () => {
  const { user } = useAuth();

  const [activeMissions, setActiveMissions] = useState<UserMission[]>([]);
  const [completedMissions, setCompletedMissions] = useState<UserMission[]>([]);
  const [availableMissions, setAvailableMissions] = useState<Mission[]>([]);

  const [loadingActive, setLoadingActive] = useState(true);
  const [loadingCompleted, setLoadingCompleted] = useState(true);
  const [loadingAvailable, setLoadingAvailable] = useState(true);

  const [expandedMissions, setExpandedMissions] = useState<Record<string, boolean>>({});
  const [redemptionLoading, setRedemptionLoading] = useState<Record<string, boolean>>({});
  const [activationLoading, setActivationLoading] = useState<Record<string, boolean>>({});

  const [error, setError] = useState<string | null>(null);

  // Funções de carregamento
  const loadActiveMissions = useCallback(async () => {
    if (!user) return;
    setLoadingActive(true);
    try {
      const missions = await getUserActiveMissions(supabase, user.id);
      setActiveMissions(missions);
    } catch (err) {
      console.error("Erro ao carregar missões ativas:", err);
      // setError("Falha ao carregar missões ativas.");
    } finally {
      setLoadingActive(false);
    }
  }, [user]);

  const loadCompletedMissions = useCallback(async () => {
    if (!user) return;
    setLoadingCompleted(true);
    try {
      // TODO: Adicionar paginação aqui no futuro
      const missions = await getUserCompletedMissions(supabase, user.id, 20); // Carrega as últimas 20
      setCompletedMissions(missions);
    } catch (err) {
      console.error("Erro ao carregar missões concluídas:", err);
      // setError("Falha ao carregar missões concluídas.");
    } finally {
      setLoadingCompleted(false);
    }
  }, [user]);

  const loadAvailableMissions = useCallback(async () => {
    if (!user) return;
    setLoadingAvailable(true);
    try {
      // Busca todos os templates de missão ativos
      const allMissionTemplates = await getAllAvailableMissions(supabase);

      // Busca as missões ativas e concluídas do usuário para filtro
      // Idealmente, estas já estariam carregadas, mas podemos re-buscar ou usar os estados
      // Para simplicidade e garantir dados frescos para o filtro, vamos buscar aqui
      // Em uma otimização, poderíamos usar os estados activeMissions e completedMissions se já estiverem populados
      const currentActiveMissions = await getUserActiveMissions(supabase, user.id);
      const currentCompletedMissions = await getUserCompletedMissions(supabase, user.id, 1000); // Pegar um número grande para filtro

      const activeMissionIds = new Set(currentActiveMissions.map(um => um.mission_id));
      const completedMissionIds = new Set(currentCompletedMissions.map(um => um.mission_id));

      const filteredAvailableMissions = allMissionTemplates.filter(template => 
        !activeMissionIds.has(template.id) && !completedMissionIds.has(template.id)
      );

      setAvailableMissions(filteredAvailableMissions);
    } catch (err) {
      console.error("Erro ao carregar missões disponíveis:", err);
      // setError("Falha ao carregar missões disponíveis.");
    } finally {
      setLoadingAvailable(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadActiveMissions();
      loadCompletedMissions();
      loadAvailableMissions();
    }
  }, [user, loadActiveMissions, loadCompletedMissions, loadAvailableMissions]);

  const toggleMissionExpand = (missionId: string) => {
    setExpandedMissions(prev => ({
      ...prev,
      [missionId]: !prev[missionId]
    }));
  };

  const handleRedeemReward = async (userMissionId: string, missionTitle: string) => {
    if (!user) {
      toast.error("Você precisa estar logado para resgatar recompensas.");
      return;
    }
    setRedemptionLoading(prev => ({ ...prev, [userMissionId]: true }));
    try {
      const result = await redeemMissionReward(supabase, user.id, userMissionId);
      if (result.success) {
        let rewardMessage = `Recompensa da missão "${missionTitle}" resgatada!`;
        if (result.xp_granted) rewardMessage += ` (+${result.xp_granted} XP)`;
        if (result.badge_id_granted) rewardMessage += ", Badge Conquistada!";
        toast.success(rewardMessage);
        // Re-carregar missões concluídas para atualizar o estado is_rewarded
        loadCompletedMissions();
        // TODO: Considerar recarregar o status geral do usuário se ele for exibido nesta página
      } else {
        toast.error(result.error || "Falha ao resgatar recompensa.");
      }
    } catch (error: any) {
      console.error("Erro ao resgatar recompensa da missão:", error);
      toast.error("Ocorreu um erro inesperado.");
    } finally {
      setRedemptionLoading(prev => ({ ...prev, [userMissionId]: false }));
    }
  };
  
  const handleActivateMission = async (missionTemplate: Mission) => {
    if (!user) {
      toast.error("Você precisa estar logado para ativar missões.");
      return;
    }
    setActivationLoading(prev => ({ ...prev, [missionTemplate.id]: true }));
    let expiresAt: Date | null = null;
    const today = new Date();

    if (missionTemplate.frequency === 'daily') {
      expiresAt = endOfDay(today);
    } else if (missionTemplate.frequency === 'weekly') {
      // Assumindo que a semana começa na Segunda e termina no Domingo para endOfWeek
      // date-fns considera Domingo como início da semana por padrão em alguns locales
      // Para garantir consistência, se necessário, ajustar o locale ou a lógica
      expiresAt = endOfWeek(today, { locale: ptBR }); // Usando ptBR para consistência
    } else if (missionTemplate.frequency === 'once' || missionTemplate.frequency === 'unlimited') {
      expiresAt = null; // Não expiram ou expiração gerenciada por start_date/end_date do template
    } else if (missionTemplate.end_date) {
        // Se for um evento ou custom com data de fim no template da missão
        expiresAt = new Date(missionTemplate.end_date);
    }
    // Adicionar lógica para 'monthly' se necessário

    try {
      const assignedMission = await assignMissionToUser(supabase, user.id, missionTemplate.id, expiresAt);
      if (assignedMission) {
        toast.success(`Missão "${missionTemplate.title}" ativada!`);
        loadActiveMissions(); // Recarregar missões ativas
        loadAvailableMissions(); // Recarregar missões disponíveis (a ativada deve sumir daqui)
      } else {
        // assignMissionToUser já loga o erro específico, aqui um toast genérico
        toast.error(`Falha ao ativar a missão "${missionTemplate.title}". Verifique os logs.`);
      }
    } catch (error: any) {
      console.error("Erro ao ativar missão:", error);
      toast.error(`Ocorreu um erro inesperado ao ativar "${missionTemplate.title}".`);
    } finally {
      setActivationLoading(prev => ({ ...prev, [missionTemplate.id]: false }));
    }
  };
  
  // Função para formatar a descrição da ação da missão (similar à de XpHistoryPage)
  const formatMissionActionDescription = (action: MissionActionRequirement): string => {
    let desc = `Realizar '${action.action_type}' ${action.quantity} vez(es).`;
    if (action.params) {
      if (action.params.unique_entity) desc += " (entidades únicas)";
      if (action.params.category) desc += ` (categoria: ${action.params.category})`;
    }
    return desc;
  };

  const renderMissionCard = (item: UserMission | Mission, type: 'active' | 'available' | 'completed') => {
    // Diferenciar se é UserMission (tem 'progress', 'user_id') ou Mission (template)
    const isUserMission = 'user_id' in item;
    const missionData = isUserMission ? (item as UserMission).mission : (item as Mission);
    const userMissionData = isUserMission ? (item as UserMission) : null;

    if (!missionData) return null; // Se for UserMission sem mission template associado (improvável)
    const mission = missionData;
    
    // Usar mission.id para 'available', e userMissionData.id para 'active'/'completed'
    const cardKey = userMissionData?.id || mission.id;
    const isExpanded = !!expandedMissions[cardKey];
    const isLoadingRedemption = userMissionData ? (redemptionLoading[userMissionData.id] || false) : false;
    const isLoadingActivation = type === 'available' ? (activationLoading[mission.id] || false) : false;

    let overallProgressPercent = 0;
    let completedActions = 0;
    const totalActions = mission.required_actions.length;

    if (type === 'active' && userMissionData) {
      mission.required_actions.forEach(reqAction => {
        const currentProgress = userMissionData.progress?.[reqAction.action_type]?.count || 0;
        if (currentProgress >= reqAction.quantity) {
          completedActions++;
        }
      });
      if (totalActions > 0) {
        overallProgressPercent = (completedActions / totalActions) * 100;
      }
    }

    let cardClasses = "p-4 border rounded-lg ";
    let titleClasses = "font-semibold text-lg ";
    let descriptionClasses = "text-sm mt-1 mb-3 "; 

    if (type === 'completed' && userMissionData) {
      cardClasses += userMissionData.is_rewarded ? 'bg-gray-100 dark:bg-gray-800 opacity-70' : 'bg-green-50 dark:bg-green-900/30';
      titleClasses += userMissionData.is_rewarded ? 'text-gray-600 dark:text-gray-400' : 'text-green-700 dark:text-green-300';
      descriptionClasses += userMissionData.is_rewarded ? 'text-gray-500' : 'text-gray-600 dark:text-gray-400';
    } else if (type === 'active') {
      cardClasses += 'bg-blue-50 dark:bg-blue-900/30';
      titleClasses += 'text-blue-700 dark:text-blue-300';
      descriptionClasses += 'text-gray-600 dark:text-gray-400';
    } else { // available
      cardClasses += 'bg-slate-50 dark:bg-slate-800/50';
      titleClasses += 'text-slate-800 dark:text-slate-200';
      descriptionClasses += 'text-muted-foreground';
    }
    
    // Determinar texto do botão de recompensa
    let rewardButtonText = "Resgatar";
    const rewardsPending: string[] = [];
    if (mission.xp_reward && mission.xp_reward > 0) rewardsPending.push(`${mission.xp_reward} XP`);
    if (mission.badge_reward_id) rewardsPending.push("Badge"); // Idealmente buscar nome do badge
    if (rewardsPending.length > 0) rewardButtonText = `Resgatar (${rewardsPending.join(' & ')})`;

    return (
      <div key={cardKey} className={cardClasses}>
        <div className="flex justify-between items-start">
          <div onClick={() => toggleMissionExpand(cardKey)} className="flex-grow cursor-pointer pr-2">
            <h3 className={titleClasses + " flex items-center"}>
              {type === 'completed' && userMissionData && <CheckCircle2 className={`h-5 w-5 mr-2 ${userMissionData.is_rewarded ? 'text-gray-500' : 'text-green-600 dark:text-green-400'}`}/>}
              {type === 'active' && <Target className="h-5 w-5 mr-2 text-blue-500"/>}
              {type === 'available' && <Search className="h-5 w-5 mr-2 text-slate-500"/>}
              {mission.title}
            </h3>
            {isExpanded && <p className={descriptionClasses}>{mission.description}</p>}
          </div>
          <div className="flex flex-col items-end flex-shrink-0 min-w-[120px]">
            <div className="flex items-center gap-2 mb-1">
                {mission.xp_reward > 0 && (
                  <UiBadge variant="outline" className="text-xs">
                    <Star className="h-3 w-3 mr-1 text-yellow-500"/> {mission.xp_reward} XP
                  </UiBadge>
                )}
                {mission.badge_reward_id && (
                    <UiBadge variant="outline" className="text-xs">
                        <Gift className="h-3 w-3 mr-1 text-purple-500"/> Badge
                    </UiBadge>
                )}
            </div>
            <Button variant="ghost" size="sm" onClick={() => toggleMissionExpand(cardKey)} className="p-1 h-auto self-end">
                {isExpanded ? <ChevronUp className="h-5 w-5 text-muted-foreground"/> : <ChevronDown className="h-5 w-5 text-muted-foreground"/>}
            </Button>
          </div>
        </div>

        {type === 'active' && (
          <>
            <Progress value={overallProgressPercent} className="w-full h-2 mt-2 mb-1" />
            <p className="text-xs text-muted-foreground text-right mb-2">{completedActions} de {totalActions} objetivos</p>
          </>
        )}

        {isExpanded && mission.required_actions && mission.required_actions.length > 0 && (
          <div className="mt-3 space-y-3 pl-2 border-l-2 border-slate-200 dark:border-slate-700">
            <p className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Objetivos:</p>
            {mission.required_actions.map((reqAction, index) => {
              const currentProgress = userMissionData?.progress?.[reqAction.action_type]?.count || 0;
              const progressPercent = (type === 'active' && userMissionData) ? Math.min((currentProgress / reqAction.quantity) * 100, 100) : 0;
              return (
                <div key={`${cardKey}-${reqAction.action_type}-${index}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center text-slate-700 dark:text-slate-300">
                      {(type === 'completed' || (type === 'active' && userMissionData && currentProgress >= reqAction.quantity) ) ? 
                        <CheckCircle2 className="h-4 w-4 mr-2 text-green-500"/> : 
                        <Target className="h-4 w-4 mr-2 text-slate-500"/>}
                      {formatMissionActionDescription(reqAction)}
                    </span>
                    {type === 'active' && userMissionData && (
                        <span className="font-mono text-xs text-muted-foreground">
                        {currentProgress}/{reqAction.quantity}
                        </span>
                    )}
                  </div>
                  {type === 'active' && userMissionData && <Progress value={progressPercent} className="w-full h-1 mt-1" />}
                </div>
              );
            })}
          </div>
        )}
        
        {type === 'active' && isExpanded && userMissionData?.expires_at && (
            <p className="text-xs text-amber-600 dark:text-amber-500 mt-3">
                Expira em: {formatDistanceToNow(new Date(userMissionData.expires_at), { addSuffix: true, locale: ptBR })}
            </p>
        )}

        {type === 'completed' && userMissionData && !userMissionData.is_rewarded && userMissionData.is_complete && (
            <div className="mt-3 pt-3 border-t border-green-200 dark:border-green-700/50">
                <Button 
                    onClick={() => handleRedeemReward(userMissionData.id, mission.title)} 
                    disabled={isLoadingRedemption}
                    size="sm"
                    className="w-full sm:w-auto bg-yellow-500 hover:bg-yellow-600 text-black"
                >
                    {isLoadingRedemption ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Coins className="mr-2 h-4 w-4" />}
                    {isLoadingRedemption ? 'Resgatando...' : rewardButtonText}
                </Button>
            </div>
        )}
         {type === 'completed' && userMissionData?.is_rewarded && (
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex items-center text-sm text-gray-500 dark:text-gray-400">
                <Award className="h-4 w-4 mr-2 text-blue-500"/> Recompensa Coletada
            </div>
        )}
        {type === 'completed' && isExpanded && userMissionData?.completed_at && (
            <p className="text-xs mt-3 text-gray-500 dark:text-gray-400">
                Concluída em: {new Date(userMissionData.completed_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}
            </p>
        )}

        {type === 'available' && isExpanded && (
            <p className="text-xs text-muted-foreground mt-3">Esta missão fica disponível para você completar quando quiser.</p>
            // Botão de "Aceitar Missão" poderia vir aqui no futuro.
        )}

        {type === 'available' && !isExpanded && (
          <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700 flex justify-end">
            <Button 
              size="sm" 
              onClick={() => handleActivateMission(mission)} 
              disabled={isLoadingActivation}
              variant="outline"
              className="bg-green-500 hover:bg-green-600 text-white dark:bg-green-600 dark:hover:bg-green-700"
            >
              {isLoadingActivation ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <PlayCircle className="mr-2 h-4 w-4" />
              )}
              Ativar Missão
            </Button>
          </div>
        )}

      </div>
    );
  };

  if (!user) {
    return (
        <div className="container mx-auto p-4 text-center">
            <p>Você precisa estar logado para ver suas missões.</p>
            {/* Adicionar link para login aqui */}
        </div>
    );
  }
  
  if (error) {
    return <div className="container mx-auto p-4 text-destructive">{error}</div>;
  }

  return (
    <div className="container mx-auto p-4 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Minhas Missões</h1>
        {/* Pode adicionar um filtro ou busca aqui no futuro */}
      </div>

      <Tabs defaultValue="active" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="active">Ativas ({loadingActive ? '...': activeMissions.length})</TabsTrigger>
          <TabsTrigger value="available">Disponíveis ({loadingAvailable ? '...': availableMissions.length})</TabsTrigger>
          <TabsTrigger value="completed">Concluídas ({loadingCompleted ? '...': completedMissions.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Missões em Andamento</CardTitle>
              <CardDescription>Complete estas missões para ganhar XP e outros prêmios.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {loadingActive ? (
                <div className="flex justify-center items-center h-40"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
              ) : activeMissions.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">Nenhuma missão ativa no momento. Volte mais tarde!</p>
              ) : (
                activeMissions.map(um => renderMissionCard(um, 'active'))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="available" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Missões Disponíveis</CardTitle>
              <CardDescription>Estas são missões que você pode iniciar quando quiser.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {loadingAvailable ? (
                <div className="flex justify-center items-center h-40"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
              ) : availableMissions.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">Não há missões gerais disponíveis no momento ou você já pegou todas!</p>
              ) : (
                availableMissions.map(m => renderMissionCard(m, 'available'))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="completed" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Histórico de Missões</CardTitle>
              <CardDescription>Suas missões finalizadas e recompensas.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {loadingCompleted ? (
                <div className="flex justify-center items-center h-40"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
              ) : completedMissions.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">Você ainda não completou nenhuma missão.</p>
              ) : (
                completedMissions.map(um => renderMissionCard(um, 'completed'))
              )}
              {/* TODO: Paginação para missões concluídas */}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default UserMissionsPage; 